import React, { useState, useEffect } from 'react';
import { ArrowLeft, Send, Loader2, Upload, X, Image as ImageIcon, Package, CheckCircle, ShoppingBag } from 'lucide-react';
import axios from 'axios';
import FutureDatePicker from '../analytics/FutureDatePicker';
import MultiProductDialog from './MultiProductDialog';

const API_URL = process.env.REACT_APP_API_URL;

function EntregaProvincia({ userId, setActiveComponent }) {
  const [formData, setFormData] = useState({
    recipient_name: '',
    recipient_phone: '',
    recipient_dni: '',
    address: '',
    agency_address: '',
    province: '',
    district: '',
    charge: '',
    deposit_advance: '',
    notes: '',
    delivery_date: ''
  });

  // Product selection state
  const [cart, setCart] = useState([]);
  const [showProductDialog, setShowProductDialog] = useState(false);

  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Image upload state
  const [showImageModal, setShowImageModal] = useState(false);
  const [proofImage, setProofImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    fetchProvinces();
  }, []);

  async function fetchProvinces() {
    try {
      const response = await axios.get(`${API_URL}/api/catalog/provinces`);
      setProvinces(response.data.provinces);
    } catch (err) {
      console.error('Error fetching provinces:', err);
      setError('Error al cargar provincias');
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }

  function handleDateChange(date) {
    setFormData(prev => ({
      ...prev,
      delivery_date: date
    }));
  }

  function handleImageSelect(e) {
    const file = e.target.files?.[0];
    if (file) {
      processImageFile(file);
    }
  }

  function processImageFile(file) {
    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona un archivo de imagen válido');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setProofImage(dataUrl);
      setImagePreview(dataUrl);
      setShowImageModal(false);
    };
    reader.readAsDataURL(file);
  }

  function handlePaste(e) {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          processImageFile(file);
        }
        break;
      }
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processImageFile(file);
    }
  }

  function handleDragOver(e) {
    e.preventDefault();
  }

  function removeImage() {
    setProofImage(null);
    setImagePreview(null);
  }

  function openImageModal() {
    setShowImageModal(true);
  }

  function closeImageModal() {
    setShowImageModal(false);
  }

  function handleSaveProducts(items) {
    setCart(items);
    setShowProductDialog(false);
  }

  function removeProduct(index) {
    setCart(prev => prev.filter((_, i) => i !== index));
  }

  function resetForm() {
    setFormData({
      recipient_name: '',
      recipient_phone: '',
      recipient_dni: '',
      address: '',
      agency_address: '',
      province: '',
      district: '',
      charge: '',
      deposit_advance: '',
      notes: '',
      delivery_date: ''
    });
    setCart([]);
    setProofImage(null);
    setImagePreview(null);
    setError(null);
  }

  function handleAddAnother() {
    resetForm();
    setShowSuccessModal(false);
  }

  function handleGoToAnalytics() {
    setShowSuccessModal(false);
    setActiveComponent('Analytics');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Prepare items for the API
      const items = cart.map(item => ({
        sku: item.sku,
        quantity: item.quantity
      }));

      const payload = {
        user_id: userId,
        recipient_name: formData.recipient_name,
        recipient_phone: formData.recipient_phone,
        recipient_dni: formData.recipient_dni,
        address: formData.address,
        agency_address: formData.agency_address,
        province: formData.province,
        district: formData.district,
        charge: formData.charge || 0,
        deposit_advance: formData.deposit_advance || 0,
        notes: formData.notes,
        delivery_date: formData.delivery_date,
        items: items,
        proof_image: proofImage
      };

      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/delivery/provincia`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error creating provincia delivery:', err);
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.response?.data?.msg ||
        'Error al crear la entrega. Por favor intenta de nuevo.'
      );
    } finally {
      setLoading(false);
    }
  }

  const isFormValid = 
    formData.recipient_name.trim() &&
    formData.recipient_phone.trim() &&
    formData.agency_address.trim() &&
    formData.province &&
    formData.district.trim() &&
    formData.delivery_date &&
    cart.length > 0;

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 space-y-4">
            <button
              type="button"
              onClick={() => setActiveComponent('Analytics')}
              className="flex items-center gap-2 text-brand-primary hover:text-brand-primary/80 transition-colors font-montserrat font-semibold mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver a Analítica
            </button>

            <div className="mb-6">
              <h2 className="text-2xl font-extrabold text-brand-primary font-montserrat">
                Nueva Entrega a Provincia
              </h2>
              <p className="text-sm text-slate-600 font-montserrat mt-1">
                Completa los datos para registrar un envío a provincia
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm font-montserrat">
                {error}
              </div>
            )}

            {/* Important Note */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800 font-montserrat">
                <strong>Nota:</strong> Los envíos a provincia tienen un tiempo de entrega de 3-5 días hábiles.
              </p>
            </div>

            {/* Product Selection */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2 font-montserrat">
                <Package className="w-4 h-4 text-brand-primary" />
                Productos del Paquete *
              </label>
              
              {cart.length === 0 ? (
                <button
                  type="button"
                  onClick={() => setShowProductDialog(true)}
                  className="w-full border-2 border-dashed border-slate-300 rounded-lg p-6 hover:border-brand-primary hover:bg-brand-primary/5 transition-all flex flex-col items-center gap-2 text-slate-600 hover:text-brand-primary font-montserrat"
                >
                  <ShoppingBag className="w-8 h-8" />
                  <span className="text-sm font-medium">Click para seleccionar productos</span>
                  <span className="text-xs text-slate-500">Selecciona los productos de tu inventario</span>
                </button>
              ) : (
                <div className="border-2 border-slate-200 rounded-lg p-4 space-y-2">
                  {/* Product chips */}
                  <div className="flex flex-wrap gap-2">
                    {cart.map((item, index) => (
                      <div
                        key={index}
                        className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-3 py-1.5 rounded-lg text-sm font-montserrat"
                      >
                        <span className="font-semibold">{item.quantity}x</span>
                        <span>{item.title}</span>
                        <button
                          type="button"
                          onClick={() => removeProduct(index)}
                          className="ml-1 text-slate-500 hover:text-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Edit button */}
                  <button
                    type="button"
                    onClick={() => setShowProductDialog(true)}
                    className="w-full mt-2 px-4 py-2 border-2 border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:border-brand-primary hover:text-brand-primary transition-all font-montserrat"
                  >
                    Editar productos
                  </button>
                </div>
              )}
            </div>

            {/* Recipient Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Nombre del Destinatario *
              </label>
              <input
                type="text"
                name="recipient_name"
                value={formData.recipient_name}
                onChange={handleChange}
                required
                className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/20 focus:border-brand-gold transition-all font-montserrat"
                placeholder="Ej: María García"
              />
            </div>

            {/* Recipient Phone */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                WhatsApp del Destinatario *
              </label>
              <input
                type="tel"
                name="recipient_phone"
                value={formData.recipient_phone}
                onChange={handleChange}
                required
                className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/20 focus:border-brand-gold transition-all font-montserrat"
                placeholder="Ej: 987654321"
              />
            </div>

            {/* Recipient DNI */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                DNI del Destinatario
              </label>
              <input
                type="text"
                name="recipient_dni"
                value={formData.recipient_dni}
                onChange={handleChange}
                maxLength="8"
                className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/20 focus:border-brand-gold transition-all font-montserrat"
                placeholder="Ej: 12345678"
              />
            </div>

            {/* Delivery Date */}
            <FutureDatePicker
              selectedDate={formData.delivery_date}
              onDateChange={handleDateChange}
            />

            {/* Province */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Provincia *
              </label>
              <select
                name="province"
                value={formData.province}
                onChange={handleChange}
                required
                className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/20 focus:border-brand-gold transition-all font-montserrat"
              >
                <option value="">Selecciona una provincia</option>
                {provinces.map(province => (
                  <option key={province} value={province}>
                    {province}
                  </option>
                ))}
              </select>
            </div>

            {/* District */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Distrito *
              </label>
              <input
                type="text"
                name="district"
                value={formData.district}
                onChange={handleChange}
  
                className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/20 focus:border-brand-gold transition-all font-montserrat"
                placeholder="Ej: Cercado de Arequipa"
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Dirección *
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/20 focus:border-brand-gold transition-all font-montserrat"
                placeholder="Ej: Calle Los Geranios 456"
              />
            </div>

            {/* Agency Address */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Dirección de Agencia *
              </label>
              <input
                type="text"
                name="agency_address"
                value={formData.agency_address}
                onChange={handleChange}
                required
                className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/20 focus:border-brand-gold transition-all font-montserrat"
                placeholder="Ej: Agencia Shalom - Jr. Lima 123"
              />
            </div>

            {/* Charge */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Cargo (Monto a Cobrar)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-montserrat text-sm">
                  S/
                </span>
                <input
                  type="number"
                  name="charge"
                  value={formData.charge}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full rounded-lg border-2 border-slate-300 bg-white pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/20 focus:border-brand-gold transition-all font-montserrat"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Deposit Advance */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Adelanto de Depósito (Monto Adelantado) *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-montserrat text-sm">
                  S/
                </span>
                <input
                  type="number"
                  name="deposit_advance"
                  value={formData.deposit_advance}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full rounded-lg border-2 border-slate-300 bg-white pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/20 focus:border-brand-gold transition-all font-montserrat"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Proof of Payment Image */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Comprobante de Pago *
              </label>
              {!imagePreview ? (
                <button
                  type="button"
                  onClick={openImageModal}
                  className="w-full border-2 border-dashed border-slate-300 rounded-lg p-6 hover:border-brand-gold hover:bg-brand-gold/5 transition-all flex flex-col items-center gap-2 text-slate-600 hover:text-brand-gold font-montserrat"
                >
                  <Upload className="w-8 h-8" />
                  <span className="text-sm font-medium">Click para subir comprobante</span>
                  <span className="text-xs text-slate-500">JPG, PNG o WEBP</span>
                </button>
              ) : (
                <div className="relative rounded-lg border-2 border-slate-300 p-2">
                  <img
                    src={imagePreview}
                    alt="Comprobante"
                    className="w-full h-48 object-contain rounded"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-4 right-4 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-colors shadow-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Notas Adicionales
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/20 focus:border-brand-gold transition-all font-montserrat resize-none"
                placeholder="Información adicional sobre la entrega..."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!isFormValid || loading}
              className="w-full bg-brand-gold hover:bg-brand-gold/90 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-3 rounded-lg transition-all font-montserrat font-semibold flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Registrando...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Registrar Envío a Provincia
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* MultiProductDialog */}
      <MultiProductDialog
        open={showProductDialog}
        onClose={() => setShowProductDialog(false)}
        onSave={handleSaveProducts}
        userId={userId}
        initialItems={cart}
        canAddProduct={true}
      />

      {/* Image Upload Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 font-montserrat">
                Subir Comprobante
              </h3>
              <button
                type="button"
                onClick={closeImageModal}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onPaste={handlePaste}
              className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-brand-gold hover:bg-brand-gold/5 transition-all cursor-pointer"
            >
              <ImageIcon className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-sm text-slate-600 font-montserrat mb-2">
                Arrastra una imagen aquí o pega desde el portapapeles
              </p>
              <p className="text-xs text-slate-500 font-montserrat mb-4">
                También puedes hacer Ctrl+V para pegar
              </p>
              <label className="inline-block">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <span className="bg-brand-gold hover:bg-brand-gold/90 text-white px-4 py-2 rounded-lg text-sm font-montserrat font-medium cursor-pointer inline-flex items-center gap-2 transition-colors">
                  <Upload className="w-4 h-4" />
                  Seleccionar archivo
                </span>
              </label>
            </div>

            <button
              type="button"
              onClick={closeImageModal}
              className="w-full mt-4 bg-slate-200 hover:bg-slate-300 text-slate-700 py-2 rounded-lg transition-colors font-montserrat font-medium"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 font-montserrat mb-2">
                ¡Envío Registrado!
              </h3>
              <p className="text-sm text-slate-600 font-montserrat">
                La entrega a provincia ha sido registrada exitosamente
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleGoToAnalytics}
                className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white py-3 rounded-lg transition-colors font-montserrat font-semibold flex items-center justify-center gap-2"
              >
                <Package className="w-5 h-5" />
                Ver Mis Entregas
              </button>

              <button
                onClick={handleAddAnother}
                className="w-full bg-brand-gold hover:bg-brand-gold/90 text-white py-3 rounded-lg transition-colors font-montserrat font-semibold"
              >
                Agregar Otro Envío a Provincia
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default EntregaProvincia;
